import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { isIOSSafariPWANeeded } from '../lib/push';

const DISMISS_KEY = '6thmate:install-dismissed-until';
const DISMISS_MS = 24 * 60 * 60 * 1000; // 1 day

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const dismissedRecently = () => {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch {
    return false;
  }
};

/**
 * Bottom-of-screen install banner. Mirrors the native Chrome "Add to Home
 * screen" prompt on Android/desktop — captures the `beforeinstallprompt`
 * event, hides the browser's default banner, and shows our own branded pill.
 *
 * On iOS Safari (where beforeinstallprompt does not exist) we show the same
 * banner with an "Install" button that opens a step-by-step Add-to-Home-Screen
 * instructions dialog. When the iOS App Store link is ready we swap the
 * on-click for a real App Store URL.
 */
const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);   // BeforeInstallPromptEvent
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;

    const iosNeed = isIOSSafariPWANeeded();
    setIsIOS(iosNeed);
    if (iosNeed) {
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (isIOS) {
      setShowIOSHelp(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setDeferred(null);
    } else {
      dismiss();
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS)); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-banner"
        style={{
          position: 'fixed',
          left: 12,
          right: 12,
          bottom: 82, // sits above the bottom nav bar (~70px)
          zIndex: 120,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          background: 'linear-gradient(180deg, #10493C 0%, #0B3B32 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          color: '#fff',
          fontFamily: 'Manrope, sans-serif',
          animation: 'installSlideIn 0.4s ease-out',
        }}
      >
        <style>{`@keyframes installSlideIn { from { transform: translateY(30px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.08)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Download size={22} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>Install 6th Mate</p>
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
            {isIOS ? 'Add to Home Screen for the full app experience' : 'Add to home screen for the best experience'}
          </p>
        </div>
        <button
          onClick={install}
          data-testid="install-banner-btn"
          style={{
            background: '#E07A5F', color: '#fff', border: 'none',
            padding: '10px 18px', borderRadius: 999, fontWeight: 700, fontSize: 14,
            cursor: 'pointer', flexShrink: 0,
          }}
        >Install</button>
        <button
          onClick={dismiss}
          data-testid="install-banner-dismiss"
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer', padding: 4, flexShrink: 0,
          }}
        >
          <X size={18} />
        </button>
      </div>

      {showIOSHelp && (
        <div
          onClick={() => setShowIOSHelp(false)}
          data-testid="install-ios-help"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 210, padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%' }}>
            <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 20, margin: '0 0 12px', color: '#111' }}>
              Add 6th Mate to your Home Screen
            </h3>
            <ol style={{ paddingLeft: 20, color: '#374151', lineHeight: 1.7, margin: 0 }}>
              <li>Tap the <strong>Share</strong> icon
                <span style={{
                  display: 'inline-block', marginLeft: 6, width: 20, height: 20,
                  border: '1px solid #6B7280', borderRadius: 4, verticalAlign: '-4px',
                }} />
                at the bottom of Safari
              </li>
              <li>Scroll and tap <strong>Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong> in the top-right corner</li>
            </ol>
            <p style={{ background: '#FEF3C7', color: '#78350F', padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.4, margin: '16px 0 0' }}>
              An Apple App Store version is coming — once it's live you'll be able to install it in one tap.
            </p>
            <button onClick={() => setShowIOSHelp(false)}
                    style={{ width: '100%', marginTop: 16, padding: '12px', borderRadius: 999, border: 'none', background: '#2C4E46', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
